import React, { ReactElement, useState } from 'react'
import { Alert, Box, Button, CircularProgress } from '@mui/material'
import { useAppSelector } from 'src/store/hooks'
import { selectRoles } from 'src/v3/settings/settings.slice'
import { backfillVideoLengths } from '../utils/course-api'

export default (): ReactElement | null => {
  let roles = useAppSelector(selectRoles)
  let isDeveloper = roles.includes('developer')

  let [backfillStatus, setBackfillStatus] = useState<
    'idle' | 'running' | 'done' | 'error'
  >('idle')
  let [backfillProgress, setBackfillProgress] = useState<{
    processed: number
    remaining: number
  }>()
  let [backfillError, setBackfillError] = useState<string>()

  let maxBackfillSteps = 1000

  let runBackfillStep = async (
    processedSoFar: number,
    afterID?: string,
    step = 0,
  ): Promise<void> => {
    if (step >= maxBackfillSteps) {
      return Promise.reject(
        new Error(
          `Backfill exceeded ${maxBackfillSteps} calls without finishing`,
        ),
      )
    }
    let response = await backfillVideoLengths(afterID)
    let processed = processedSoFar + response.processedCount
    setBackfillProgress({ processed, remaining: response.remainingCount })
    return response.hasMore
      ? runBackfillStep(processed, response.nextCursor ?? undefined, step + 1)
      : undefined
  }

  let runVideoLengthBackfill = async () => {
    setBackfillStatus('running')
    setBackfillProgress(undefined)
    setBackfillError(undefined)
    try {
      await runBackfillStep(0)
      setBackfillStatus('done')
    } catch (err) {
      setBackfillStatus('error')
      setBackfillError(err instanceof Error ? err.message : 'Backfill failed')
    }
  }

  if (!isDeveloper) return null

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: 1,
        flexShrink: 0,
      }}
    >
      <Button
        variant="outlined"
        size="small"
        onClick={runVideoLengthBackfill}
        disabled={backfillStatus === 'running'}
        startIcon={
          backfillStatus === 'running' ? (
            <CircularProgress size={16} />
          ) : undefined
        }
        aria-label="Backfill video lengths"
      >
        {backfillStatus === 'running'
          ? 'Backfilling video lengths…'
          : 'Backfill video lengths'}
      </Button>
      {backfillStatus === 'running' && backfillProgress && (
        <Alert severity="info">
          Processed {backfillProgress.processed} videos,{' '}
          {backfillProgress.remaining} remaining…
        </Alert>
      )}
      {backfillStatus === 'done' && (
        <Alert severity="success" onClose={() => setBackfillStatus('idle')}>
          Backfill complete
          {backfillProgress
            ? ` — ${backfillProgress.processed} videos processed.`
            : '.'}
        </Alert>
      )}
      {backfillStatus === 'error' && (
        <Alert severity="error" onClose={() => setBackfillStatus('idle')}>
          Backfill failed{backfillError ? `: ${backfillError}` : '.'}
        </Alert>
      )}
    </Box>
  )
}
